import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Wallet, Plus, Trash2, Settings, CalendarIcon, ArrowUpCircle, ArrowDownCircle,
  TrendingUp, TrendingDown, RefreshCw, Search, Filter, FileText, Pencil, Check, X
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankTransactionsPage() {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('transactions');
  
  // Data states
  const [balance, setBalance] = useState({
    opening_balance: 0,
    total_receipts: 0,
    total_payments: 0,
    current_balance: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [forexDialogOpen, setForexDialogOpen] = useState(false);  // New dedicated forex dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [classificationsDialogOpen, setClassificationsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null); // null = create mode, object = edit mode
  
  // Form states
  const [transactionForm, setTransactionForm] = useState({
    transaction_date: new Date(),
    transaction_type: 'payment',
    utr_number: '',
    line_items: [createEmptyLineItem()]
  });
  
  // Dedicated forex payment form
  const [forexForm, setForexForm] = useState({
    transaction_date: new Date(),
    utr_number: '',
    classification_id: '',
    classification_name: '',
    supplier_id: '',
    supplier_name: '',
    inr_amount: '',
    forex_currency: 'USD',
    forex_amount: '',
    forex_pi_number: '',
    forex_payment_purpose: 'advance'
  });
  
  const [settingsForm, setSettingsForm] = useState({
    opening_balance: 0,
    opening_balance_date: new Date(),
    bank_name: ''
  });
  
  const [newClassification, setNewClassification] = useState({
    name: '',
    account_type: 'P&L',
    linked_entity_type: null
  });
  
  // State for editing a classification
  const [editingClassification, setEditingClassification] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });
  const [typeFilter, setTypeFilter] = useState('all');

  function createEmptyLineItem() {
    return {
      classification_id: '',
      classification_name: '',
      linked_entity_type: null,
      linked_entity_id: '',
      linked_entity_name: '',
      amount: '',
      account_type: '',
      remarks: ''
    };
  }

  // Memoized sorted options for searchable dropdowns
  const classificationOptions = useMemo(() => {
    return [...classifications]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ value: c.id, label: c.name }));
  }, [classifications]);

  const clientOptions = useMemo(() => {
    return [...clients]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(c => ({ value: c.id, label: c.name }));
  }, [clients]);

  const supplierOptions = useMemo(() => {
    return [...suppliers]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(s => ({ value: s.id, label: s.name }));
  }, [suppliers]);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-transactions/balance`, {
        headers: getAuthHeader()
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  }, [getAuthHeader]);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('start_date', format(dateFilter.start, 'yyyy-MM-dd'));
      if (dateFilter.end) params.append('end_date', format(dateFilter.end, 'yyyy-MM-dd'));
      if (typeFilter !== 'all') params.append('transaction_type', typeFilter);
      
      const response = await axios.get(`${API_URL}/api/bank-transactions/transactions?${params}`, {
        headers: getAuthHeader()
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, [getAuthHeader, dateFilter, typeFilter]);

  const fetchClassifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-transactions/classifications`, {
        headers: getAuthHeader()
      });
      setClassifications(response.data.classifications || []);
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
    }
  }, [getAuthHeader]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-transactions/settings`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setSettingsForm({
          opening_balance: response.data.opening_balance || 0,
          opening_balance_date: response.data.opening_balance_date ? new Date(response.data.opening_balance_date) : new Date(),
          bank_name: response.data.bank_name || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, [getAuthHeader]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, {
        headers: getAuthHeader()
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [getAuthHeader]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers`, {
        headers: getAuthHeader()
      });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBalance(),
        fetchTransactions(),
        fetchClassifications(),
        fetchSettings(),
        fetchClients(),
        fetchSuppliers()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchBalance, fetchTransactions, fetchClassifications, fetchSettings, fetchClients, fetchSuppliers]);

  useEffect(() => {
    fetchTransactions();
  }, [dateFilter, typeFilter, fetchTransactions]);

  const handleClassificationChange = (index, classificationId) => {
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) return;

    // Use the linked_entity_type stored in the classification
    const linkedEntityType = classification.linked_entity_type || null;

    setTransactionForm(prev => {
      const newLineItems = [...prev.line_items];
      newLineItems[index] = {
        ...newLineItems[index],
        classification_id: classificationId,
        classification_name: classification.name,
        account_type: classification.account_type,
        linked_entity_type: linkedEntityType,
        linked_entity_id: '',
        linked_entity_name: ''
      };
      return { ...prev, line_items: newLineItems };
    });
  };

  const handleLinkedEntityChange = (index, entityId) => {
    const lineItem = transactionForm.line_items[index];
    let entityName = '';
    
    if (lineItem.linked_entity_type === 'client') {
      const client = clients.find(c => c.id === entityId);
      entityName = client?.name || '';
    } else if (lineItem.linked_entity_type === 'supplier') {
      const supplier = suppliers.find(s => s.id === entityId);
      entityName = supplier?.name || '';
    }

    setTransactionForm(prev => {
      const newLineItems = [...prev.line_items];
      newLineItems[index] = {
        ...newLineItems[index],
        linked_entity_id: entityId,
        linked_entity_name: entityName
      };
      return { ...prev, line_items: newLineItems };
    });
  };

  const handleAmountChange = (index, value) => {
    setTransactionForm(prev => {
      const newLineItems = [...prev.line_items];
      newLineItems[index] = { ...newLineItems[index], amount: value };
      return { ...prev, line_items: newLineItems };
    });
  };

  const handleRemarksChange = (index, value) => {
    setTransactionForm(prev => {
      const newLineItems = [...prev.line_items];
      newLineItems[index] = { ...newLineItems[index], remarks: value };
      return { ...prev, line_items: newLineItems };
    });
  };

  const addLineItem = () => {
    setTransactionForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, createEmptyLineItem()]
    }));
  };

  const removeLineItem = (index) => {
    if (transactionForm.line_items.length === 1) {
      toast.error('At least one line item is required');
      return;
    }
    setTransactionForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return transactionForm.line_items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const calculateBalanceAfter = () => {
    const total = calculateTotal();
    if (transactionForm.transaction_type === 'receipt') {
      return balance.current_balance + total;
    } else {
      return balance.current_balance - total;
    }
  };

  const handleSaveTransaction = async () => {
    // Validation
    if (!transactionForm.transaction_date) {
      toast.error('Please select a transaction date');
      return;
    }

    const validLineItems = transactionForm.line_items.filter(item => 
      item.classification_id && parseFloat(item.amount) > 0
    );

    if (validLineItems.length === 0) {
      toast.error('Please add at least one line item with classification and amount');
      return;
    }

    // Check linked entities
    for (const item of validLineItems) {
      if (item.linked_entity_type && !item.linked_entity_id) {
        toast.error(`Please select ${item.linked_entity_type === 'client' ? 'a client' : 'a supplier'} for ${item.classification_name}`);
        return;
      }
    }

    try {
      const payload = {
        transaction_date: format(transactionForm.transaction_date, 'yyyy-MM-dd'),
        transaction_type: transactionForm.transaction_type,
        utr_number: transactionForm.utr_number || null,
        line_items: validLineItems.map(item => ({
          classification_id: item.classification_id,
          classification_name: item.classification_name,
          linked_entity_type: item.linked_entity_type,
          linked_entity_id: item.linked_entity_id || null,
          linked_entity_name: item.linked_entity_name || null,
          amount: parseFloat(item.amount),
          account_type: item.account_type,
          remarks: item.remarks || null
        }))
      };

      if (editingTransaction) {
        // Update existing transaction
        await axios.put(`${API_URL}/api/bank-transactions/transactions/${editingTransaction.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Transaction updated successfully');
      } else {
        // Create new transaction
        await axios.post(`${API_URL}/api/bank-transactions/transactions`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Transaction recorded successfully');
      }

      setTransactionDialogOpen(false);
      setEditingTransaction(null);
      setTransactionForm({
        transaction_date: new Date(),
        transaction_type: 'payment',
        utr_number: '',
        line_items: [createEmptyLineItem()]
      });
      fetchTransactions();
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    }
  };

  // Save forex payment (dedicated form)
  const saveForexPayment = async () => {
    // Validate required fields
    if (!forexForm.classification_id) {
      toast.error('Please select a classification');
      return;
    }
    if (!forexForm.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!forexForm.inr_amount || parseFloat(forexForm.inr_amount) <= 0) {
      toast.error('Please enter a valid INR amount');
      return;
    }
    if (!forexForm.forex_amount || parseFloat(forexForm.forex_amount) <= 0) {
      toast.error('Please enter a valid forex amount');
      return;
    }
    if (!forexForm.forex_pi_number) {
      toast.error('Please enter the PI number');
      return;
    }

    // Get the selected classification
    const selectedClassification = classifications.find(c => c.id === forexForm.classification_id);
    if (!selectedClassification) {
      toast.error('Invalid classification selected');
      return;
    }

    try {
      const payload = {
        transaction_date: format(forexForm.transaction_date, 'yyyy-MM-dd'),
        transaction_type: 'payment',
        utr_number: forexForm.utr_number || null,
        is_forex_payment: true,
        forex_supplier_id: forexForm.supplier_id,
        forex_supplier_name: forexForm.supplier_name,
        forex_currency: forexForm.forex_currency,
        forex_amount: parseFloat(forexForm.forex_amount),
        forex_pi_number: forexForm.forex_pi_number,
        forex_payment_purpose: forexForm.forex_payment_purpose,
        line_items: [{
          classification_id: selectedClassification.id,
          classification_name: selectedClassification.name,
          linked_entity_type: 'supplier',
          linked_entity_id: forexForm.supplier_id,
          linked_entity_name: forexForm.supplier_name,
          amount: parseFloat(forexForm.inr_amount),
          account_type: selectedClassification.account_type || 'P&L',
          remarks: `Forex Payment - PI: ${forexForm.forex_pi_number}`
        }]
      };

      const response = await axios.post(`${API_URL}/api/bank-transactions/transactions`, payload, {
        headers: getAuthHeader()
      });

      if (response.data.forex_payment_created) {
        toast.success(`Forex Payment ${response.data.forex_payment_created.forex_payment_number} recorded successfully`);
      } else {
        toast.success('Forex payment recorded successfully');
      }

      setForexDialogOpen(false);
      setForexForm({
        transaction_date: new Date(),
        utr_number: '',
        classification_id: '',
        classification_name: '',
        supplier_id: '',
        supplier_name: '',
        inr_amount: '',
        forex_currency: 'USD',
        forex_amount: '',
        forex_pi_number: '',
        forex_payment_purpose: 'advance'
      });
      fetchTransactions();
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save forex payment');
    }
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    // Check if current user is the creator
    if (transaction.created_by_id && user?.id !== transaction.created_by_id) {
      toast.error(`Only ${transaction.created_by_name || 'the creator'} can edit this transaction`);
      return;
    }

    // Don't allow editing forex transactions through normal form
    if (transaction.is_forex_payment) {
      toast.error('Forex payment transactions cannot be edited through this form');
      return;
    }

    setEditingTransaction(transaction);
    setTransactionForm({
      transaction_date: new Date(transaction.transaction_date),
      transaction_type: transaction.transaction_type,
      utr_number: transaction.utr_number || '',
      line_items: transaction.line_items?.map(item => ({
        classification_id: item.classification_id,
        classification_name: item.classification_name,
        linked_entity_type: item.linked_entity_type || null,
        linked_entity_id: item.linked_entity_id || '',
        linked_entity_name: item.linked_entity_name || '',
        amount: item.amount?.toString() || '',
        account_type: item.account_type,
        remarks: item.remarks || ''
      })) || [createEmptyLineItem()]
    });
    setTransactionDialogOpen(true);
  };

  // Handle delete transaction
  const handleDeleteTransaction = async (transaction) => {
    // Check if current user is the creator
    if (transaction.created_by_id && user?.id !== transaction.created_by_id) {
      toast.error(`Only ${transaction.created_by_name || 'the creator'} can delete this transaction`);
      return;
    }

    if (!window.confirm(`Delete transaction ${transaction.transaction_number}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/api/bank-transactions/transactions/${transaction.id}`, {
        headers: getAuthHeader()
      });
      
      toast.success('Transaction deleted');
      
      // Show info about deleted sales receipts if any
      if (response.data.deleted_sales_receipts?.length > 0) {
        toast.info(`Also deleted ${response.data.deleted_sales_receipts.length} linked sales receipt(s)`);
      }
      
      fetchTransactions();
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/bank-transactions/settings`, {
        opening_balance: parseFloat(settingsForm.opening_balance) || 0,
        opening_balance_date: format(settingsForm.opening_balance_date, 'yyyy-MM-dd'),
        bank_name: settingsForm.bank_name || null
      }, {
        headers: getAuthHeader()
      });

      toast.success('Settings saved successfully');
      setSettingsDialogOpen(false);
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleAddClassification = async () => {
    if (!newClassification.name.trim()) {
      toast.error('Please enter a classification name');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/bank-transactions/classifications`, {
        name: newClassification.name.trim(),
        account_type: newClassification.account_type,
        linked_entity_type: newClassification.linked_entity_type
      }, {
        headers: getAuthHeader()
      });

      toast.success('Classification added');
      setNewClassification({ name: '', account_type: 'P&L', linked_entity_type: null });
      fetchClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add classification');
    }
  };

  // Handle update classification
  const handleUpdateClassification = async () => {
    if (!editingClassification) return;
    
    if (!editingClassification.name?.trim()) {
      toast.error('Please enter a classification name');
      return;
    }

    try {
      await axios.patch(`${API_URL}/api/bank-transactions/classifications/${editingClassification.id}`, {
        name: editingClassification.name.trim(),
        account_type: editingClassification.account_type,
        linked_entity_type: editingClassification.linked_entity_type
      }, {
        headers: getAuthHeader()
      });

      toast.success('Classification updated');
      setEditingClassification(null);
      fetchClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update classification');
    }
  };

  // Handle delete classification
  const handleDeleteClassification = async (classification) => {
    if (!window.confirm(`Delete classification "${classification.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/bank-transactions/classifications/${classification.id}`, {
        headers: getAuthHeader()
      });

      toast.success('Classification deleted');
      fetchClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete classification');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="bank-transactions-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bank Transactions</h1>
            <p className="text-muted-foreground">Record and manage bank receipts & payments</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" onClick={() => setTransactionDialogOpen(true)} data-testid="record-transaction-btn">
              <Plus className="w-4 h-4 mr-2" />
              Record Transaction
            </Button>
            <Button onClick={() => setForexDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="record-forex-btn">
              <Plus className="w-4 h-4 mr-2" />
              Record Forex Payment
            </Button>
          </div>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10 rounded-full">
                  <Wallet className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-sm text-slate-300">Current Bank Balance</p>
                  <p className="text-3xl font-bold">{formatCurrency(balance.current_balance)}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-8 text-right">
                <div>
                  <p className="text-xs text-slate-400">Opening Balance</p>
                  <p className="text-lg font-semibold">{formatCurrency(balance.opening_balance)}</p>
                </div>
                <div>
                  <p className="text-xs text-green-400 flex items-center justify-end gap-1">
                    <ArrowUpCircle className="w-3 h-3" /> Total Receipts
                  </p>
                  <p className="text-lg font-semibold text-green-400">+{formatCurrency(balance.total_receipts)}</p>
                </div>
                <div>
                  <p className="text-xs text-red-400 flex items-center justify-end gap-1">
                    <ArrowDownCircle className="w-3 h-3" /> Total Payments
                  </p>
                  <p className="text-lg font-semibold text-red-400">-{formatCurrency(balance.total_payments)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => {
          if (val === 'classifications') {
            setClassificationsDialogOpen(true);
            // Don't change the tab, just open the dialog
          } else {
            setActiveTab(val);
          }
        }}>
          <TabsList>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="classifications">Classifications</TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="receipt">Receipts</SelectItem>
                      <SelectItem value="payment">Payments</SelectItem>
                    </SelectContent>
                  </Select>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateFilter.start ? format(dateFilter.start, 'dd/MM/yyyy') : 'Start Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFilter.start}
                        onSelect={(date) => setDateFilter(prev => ({ ...prev, start: date }))}
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateFilter.end ? format(dateFilter.end, 'dd/MM/yyyy') : 'End Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFilter.end}
                        onSelect={(date) => setDateFilter(prev => ({ ...prev, end: date }))}
                      />
                    </PopoverContent>
                  </Popover>
                  {(dateFilter.start || dateFilter.end || typeFilter !== 'all') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setDateFilter({ start: null, end: null });
                        setTypeFilter('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead>Linked To</TableHead>
                      <TableHead>GSTIN</TableHead>
                      <TableHead className="text-right text-green-700">Receipts</TableHead>
                      <TableHead className="text-right text-red-700">Payments</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="text-center w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.flatMap((txn, txnIdx) => {
                        const lineItems = txn.line_items || [];
                        const isMultiLine = lineItems.length > 1;
                        const lineBalances = txn.line_item_balances || [];
                        
                        return lineItems.map((item, idx) => {
                          const isFirstRow = idx === 0;
                          const isLastRow = idx === lineItems.length - 1;
                          // Get per-line-item balance, fallback to transaction balance for backwards compatibility
                          const lineBalance = lineBalances[idx] !== undefined 
                            ? lineBalances[idx] 
                            : (isLastRow ? txn.running_balance : undefined);
                          
                          return (
                            <TableRow 
                              key={`${txn.id}-${idx}`}
                              className={`
                                ${isMultiLine ? 'bg-slate-50/50' : ''}
                                ${isMultiLine && isLastRow ? 'border-b-2 border-slate-200' : ''}
                              `}
                            >
                              <TableCell className="text-sm">
                                {isFirstRow ? formatDate(txn.transaction_date) : ''}
                              </TableCell>
                              <TableCell className="font-medium">
                                {!isFirstRow && isMultiLine && (
                                  <span className="text-muted-foreground mr-1">↳</span>
                                )}
                                {item.classification_name}
                              </TableCell>
                              <TableCell className="text-sm">
                                {item.linked_entity_name || <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {isFirstRow && txn.client_gstin ? txn.client_gstin : ''}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-green-600">
                                {txn.transaction_type === 'receipt' ? formatCurrency(item.amount) : ''}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-red-600">
                                {txn.transaction_type === 'payment' ? formatCurrency(item.amount) : ''}
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {lineBalance !== undefined ? formatCurrency(lineBalance) : ''}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {item.account_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                                {item.remarks || ''}
                              </TableCell>
                              <TableCell className="text-center">
                                {isFirstRow && (
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => handleEditTransaction(txn)}
                                      title="Edit transaction"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteTransaction(txn)}
                                      title="Delete transaction"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Classification-wise Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Reports coming soon...</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Record Transaction Dialog */}
        <Dialog open={transactionDialogOpen} onOpenChange={(open) => {
          setTransactionDialogOpen(open);
          if (!open) {
            setEditingTransaction(null);
            setTransactionForm({
              transaction_date: new Date(),
              transaction_type: 'payment',
              utr_number: '',
              line_items: [createEmptyLineItem()],
              is_forex_payment: false,
              forex_supplier_id: '',
              forex_supplier_name: ''
            });
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTransaction ? 'Edit Bank Transaction' : 'Record Bank Transaction'}</DialogTitle>
              <DialogDescription>
                {editingTransaction 
                  ? `Editing transaction ${editingTransaction.transaction_number}` 
                  : 'Record a new receipt or payment transaction'}
              </DialogDescription>
            </DialogHeader>

            {/* Current Balance Info */}
            <div className="bg-slate-100 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-slate-600" />
                  <span className="font-medium">Current Bank Balance:</span>
                </div>
                <span className="text-xl font-bold">{formatCurrency(balance.current_balance)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Opening: {formatCurrency(balance.opening_balance)} | +Receipts: {formatCurrency(balance.total_receipts)} | -Payments: {formatCurrency(balance.total_payments)}
              </div>
            </div>

            <div className="space-y-6">
              {/* Date and Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transaction Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {transactionForm.transaction_date ? format(transactionForm.transaction_date, 'dd/MM/yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={transactionForm.transaction_date}
                        onSelect={(date) => setTransactionForm(prev => ({ ...prev, transaction_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Transaction Type *</Label>
                  <div className="flex gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="transaction_type"
                        value="receipt"
                        checked={transactionForm.transaction_type === 'receipt'}
                        onChange={(e) => setTransactionForm(prev => ({ ...prev, transaction_type: e.target.value }))}
                        className="w-4 h-4"
                      />
                      <span className="flex items-center gap-1 text-green-600">
                        <ArrowUpCircle className="w-4 h-4" /> Receipt
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="transaction_type"
                        value="payment"
                        checked={transactionForm.transaction_type === 'payment'}
                        onChange={(e) => setTransactionForm(prev => ({ ...prev, transaction_type: e.target.value }))}
                        className="w-4 h-4"
                      />
                      <span className="flex items-center gap-1 text-red-600">
                        <ArrowDownCircle className="w-4 h-4" /> Payment
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* UTR/Reference Number */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="utr_number">UTR / Reference Number</Label>
                  <Input
                    id="utr_number"
                    placeholder="Enter UTR or reference number"
                    value={transactionForm.utr_number}
                    onChange={(e) => setTransactionForm(prev => ({ ...prev, utr_number: e.target.value }))}
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Line Items</Label>
                  <Button variant="outline" size="sm" onClick={() => setClassificationsDialogOpen(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Classifications
                  </Button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Classification *</TableHead>
                        <TableHead>Linked To</TableHead>
                        <TableHead className="w-32">Amount (₹) *</TableHead>
                        <TableHead className="w-20">Type</TableHead>
                        <TableHead>Remarks</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactionForm.line_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell>
                            <SearchableSelect
                              options={classificationOptions}
                              value={item.classification_id}
                              onValueChange={(value) => handleClassificationChange(index, value)}
                              placeholder="Select Classification..."
                              searchPlaceholder="Search classifications..."
                              emptyText="No classifications found"
                            />
                          </TableCell>
                          <TableCell>
                            {item.linked_entity_type === 'client' ? (
                              <SearchableSelect
                                options={clientOptions}
                                value={item.linked_entity_id}
                                onValueChange={(value) => handleLinkedEntityChange(index, value)}
                                placeholder="Select Client..."
                                searchPlaceholder="Search clients..."
                                emptyText="No clients found"
                              />
                            ) : item.linked_entity_type === 'supplier' ? (
                              <SearchableSelect
                                options={supplierOptions}
                                value={item.linked_entity_id}
                                onValueChange={(value) => handleLinkedEntityChange(index, value)}
                                placeholder="Select Supplier..."
                                searchPlaceholder="Search suppliers..."
                                emptyText="No suppliers found"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.amount}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.account_type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.remarks}
                              onChange={(e) => handleRemarksChange(index, e.target.value)}
                              placeholder="Optional"
                              className="text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Button variant="outline" onClick={addLineItem} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line Item
                </Button>
              </div>

              {/* Totals */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">This Transaction:</span>
                  <span className={`text-xl font-bold ${transactionForm.transaction_type === 'receipt' ? 'text-green-600' : 'text-red-600'}`}>
                    {transactionForm.transaction_type === 'receipt' ? '+' : '-'}{formatCurrency(calculateTotal())}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="font-medium">Balance After This:</span>
                  <span className="text-xl font-bold">{formatCurrency(calculateBalanceAfter())}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setTransactionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTransaction}>
                {editingTransaction ? 'Update Transaction' : 'Save Transaction'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dedicated Forex Payment Dialog */}
        <Dialog open={forexDialogOpen} onOpenChange={setForexDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Wallet className="w-5 h-5 text-blue-600" />
                </div>
                Record Forex Payment
              </DialogTitle>
              <DialogDescription>
                Record an international payment with forex details
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Bank Transaction Details */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Bank Transaction Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Transaction Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(forexForm.transaction_date, 'dd/MM/yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={forexForm.transaction_date}
                          onSelect={(date) => setForexForm(prev => ({ ...prev, transaction_date: date || new Date() }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>UTR / Reference Number</Label>
                    <Input
                      placeholder="Enter UTR or reference number"
                      value={forexForm.utr_number}
                      onChange={(e) => setForexForm(prev => ({ ...prev, utr_number: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Classification & Supplier */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Classification & Supplier</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Classification *</Label>
                    <SearchableSelect
                      options={classificationOptions}
                      value={forexForm.classification_id}
                      onValueChange={(value) => {
                        const classification = classifications.find(c => c.id === value);
                        setForexForm(prev => ({
                          ...prev,
                          classification_id: value,
                          classification_name: classification?.name || ''
                        }));
                      }}
                      placeholder="Select Classification..."
                      searchPlaceholder="Search classifications..."
                      emptyText="No classifications found"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Import Supplier *</Label>
                    <SearchableSelect
                      options={supplierOptions}
                      value={forexForm.supplier_id}
                      onValueChange={(value) => {
                        const supplier = suppliers.find(s => s.id === value);
                        setForexForm(prev => ({
                          ...prev,
                          supplier_id: value,
                          supplier_name: supplier?.name || ''
                        }));
                      }}
                      placeholder="Select Import Supplier..."
                      searchPlaceholder="Search suppliers..."
                      emptyText="No suppliers found"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>INR Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Amount paid in INR"
                    value={forexForm.inr_amount}
                    onChange={(e) => setForexForm(prev => ({ ...prev, inr_amount: e.target.value }))}
                    data-testid="forex-inr-amount"
                  />
                </div>
              </div>

              {/* Forex Details */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900">Forex Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-blue-800">PI Number *</Label>
                    <Input
                      placeholder="Proforma Invoice Number"
                      value={forexForm.forex_pi_number}
                      onChange={(e) => setForexForm(prev => ({ ...prev, forex_pi_number: e.target.value }))}
                      data-testid="forex-pi-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-blue-800">Payment Purpose *</Label>
                    <Select
                      value={forexForm.forex_payment_purpose}
                      onValueChange={(value) => setForexForm(prev => ({ ...prev, forex_payment_purpose: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="advance">Advance Payment</SelectItem>
                        <SelectItem value="part_payment">Part Payment</SelectItem>
                        <SelectItem value="final_payment">Final Payment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-blue-800">Currency *</Label>
                    <Select
                      value={forexForm.forex_currency}
                      onValueChange={(value) => setForexForm(prev => ({ ...prev, forex_currency: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                        <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                        <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                        <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                        <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-blue-800">Forex Amount *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 10000.00"
                      value={forexForm.forex_amount}
                      onChange={(e) => setForexForm(prev => ({ ...prev, forex_amount: e.target.value }))}
                      data-testid="forex-amount-input"
                    />
                  </div>
                </div>

                {/* Auto-calculated Exchange Rate */}
                {forexForm.inr_amount && forexForm.forex_amount && 
                 parseFloat(forexForm.inr_amount) > 0 && parseFloat(forexForm.forex_amount) > 0 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700 font-medium">Exchange Rate (auto-calculated)</span>
                      <span className="text-xl font-bold text-green-800">
                        {(parseFloat(forexForm.inr_amount) / parseFloat(forexForm.forex_amount)).toFixed(4)}
                      </span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      ₹{parseFloat(forexForm.inr_amount).toLocaleString('en-IN')} ÷ {forexForm.forex_amount} {forexForm.forex_currency}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setForexDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveForexPayment} className="bg-blue-600 hover:bg-blue-700" data-testid="save-forex-btn">
                Record Forex Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bank Account Settings</DialogTitle>
              <DialogDescription>Configure opening balance and bank details</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Opening Balance *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settingsForm.opening_balance}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, opening_balance: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label>As of Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {settingsForm.opening_balance_date ? format(settingsForm.opening_balance_date, 'dd/MM/yyyy') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={settingsForm.opening_balance_date}
                      onSelect={(date) => setSettingsForm(prev => ({ ...prev, opening_balance_date: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Bank Name (Optional)</Label>
                <Input
                  value={settingsForm.bank_name}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  placeholder="e.g., HDFC Current Account"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings}>Save Settings</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Classifications Dialog */}
        <Dialog open={classificationsDialogOpen} onOpenChange={setClassificationsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Account Classifications</DialogTitle>
              <DialogDescription>Add and manage account classifications for transactions</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Add New */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <Label className="text-sm font-semibold mb-3 block">Add New Classification</Label>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    value={newClassification.name}
                    onChange={(e) => setNewClassification(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Classification Name"
                    className="flex-1 min-w-[200px]"
                  />
                  <Select
                    value={newClassification.account_type}
                    onValueChange={(value) => setNewClassification(prev => ({ ...prev, account_type: value }))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P&L">P&L</SelectItem>
                      <SelectItem value="Balance Sheet">Balance Sheet</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={newClassification.linked_entity_type || 'none'}
                    onValueChange={(value) => setNewClassification(prev => ({ 
                      ...prev, 
                      linked_entity_type: value === 'none' ? null : value 
                    }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Linked To" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Linking</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddClassification}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Tip: Link to "Client" for receipts from customers, or "Supplier" for payments to vendors
                </p>
              </div>

              {/* Existing Classifications */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Name</TableHead>
                      <TableHead className="w-32">Type</TableHead>
                      <TableHead className="w-40">Linked Entity</TableHead>
                      <TableHead className="w-24 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No classifications added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      classifications.map(c => (
                        <TableRow key={c.id}>
                          {editingClassification?.id === c.id ? (
                            // Edit mode
                            <>
                              <TableCell>
                                <Input
                                  value={editingClassification.name}
                                  onChange={(e) => setEditingClassification(prev => ({ ...prev, name: e.target.value }))}
                                  className="h-8"
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={editingClassification.account_type}
                                  onValueChange={(value) => setEditingClassification(prev => ({ ...prev, account_type: value }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="P&L">P&L</SelectItem>
                                    <SelectItem value="Balance Sheet">Balance Sheet</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={editingClassification.linked_entity_type || 'none'}
                                  onValueChange={(value) => setEditingClassification(prev => ({ 
                                    ...prev, 
                                    linked_entity_type: value === 'none' ? null : value 
                                  }))}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No Linking</SelectItem>
                                    <SelectItem value="client">Client</SelectItem>
                                    <SelectItem value="supplier">Supplier</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={handleUpdateClassification}
                                    title="Save"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setEditingClassification(null)}
                                    title="Cancel"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          ) : (
                            // View mode
                            <>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{c.account_type}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={c.linked_entity_type ? 'secondary' : 'outline'} className="text-xs">
                                  {c.linked_entity_type === 'client' ? 'Client' : 
                                   c.linked_entity_type === 'supplier' ? 'Supplier' : 'No Linking'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setEditingClassification({ ...c })}
                                    title="Edit"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteClassification(c)}
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => {
                setClassificationsDialogOpen(false);
                setEditingClassification(null);
              }}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
